import { useState } from 'react';
import { Alert, Box, Button, Grid } from '@mui/material';
import { Tune, Settings } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PageHeader } from '../../components/common/PageHeader';
import { dashboardApi, errorMessage, type MyDashboard } from './dashboardApi';
import { WidgetCard, WidgetGrid } from './WidgetGrid';
import { LayoutEditorDialog } from './LayoutEditorDialog';

const SOURCE_TEXT: Record<MyDashboard['source'], (d: MyDashboard) => string> = {
  PERSONAL: () => 'Your own layout',
  USER_TEMPLATE: (d) => `Layout assigned to you: ${d.templateName}`,
  ROLE_TEMPLATE: (d) => `Layout for your role: ${d.templateName}`,
  SYSTEM_DEFAULT: () => 'Default layout',
};

// The dashboard is one framework: the server resolves WHICH widgets this user gets (personal layout -> assigned template ->
// role template -> default, each filtered to what they may view) and loads their data in one call. Every control below
// follows the capabilities the server reports; the server re-checks each action regardless.
export function Dashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [customizing, setCustomizing] = useState(false);

  const { data, isLoading, error } = useQuery({ queryKey: ['dashboard'], queryFn: dashboardApi.mine, refetchInterval: 120_000 });
  // The "add a widget" list is only needed once the user opens the customizer.
  const catalog = useQuery({ queryKey: ['dashboard-catalog'], queryFn: dashboardApi.catalog, enabled: customizing });

  const save = useMutation({
    mutationFn: dashboardApi.saveLayout,
    onSuccess: () => { enqueueSnackbar('Dashboard layout saved', { variant: 'success' }); setCustomizing(false); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
    onError: (e) => enqueueSnackbar(errorMessage(e), { variant: 'error' }),
  });
  const reset = useMutation({
    mutationFn: dashboardApi.resetLayout,
    onSuccess: () => { enqueueSnackbar('Back to the assigned / default layout', { variant: 'success' }); setCustomizing(false); qc.invalidateQueries({ queryKey: ['dashboard'] }); },
    onError: (e) => enqueueSnackbar(errorMessage(e), { variant: 'error' }),
  });

  if (error) return <Alert severity="error">{errorMessage(error) || 'Failed to load dashboard'}</Alert>;

  const caps = data?.capabilities;
  const canSetup = !!caps && (caps.createTemplates || caps.manageTemplates || caps.manageAssignments);
  const actions = [
    ...(caps?.customize ? [{ label: 'Customize', onClick: () => setCustomizing(true), icon: <Tune />, variant: 'outlined' as const }] : []),
    ...(canSetup ? [{ label: 'Templates', onClick: () => navigate('/settings/dashboard-templates'), icon: <Settings />, variant: 'text' as const }] : []),
  ];

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle={`${new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}${data ? ` — ${SOURCE_TEXT[data.source](data)}` : ''}`}
        actions={actions}
      />

      {isLoading ? (
        <Grid container spacing={2}>{[0, 1, 2, 3].map((i) => <Grid key={i} item xs={12} md={6}><WidgetCard loading /></Grid>)}</Grid>
      ) : data && data.widgets.length === 0 ? (
        <Alert severity="info">
          There are no dashboard widgets available for your access yet. Widgets appear as your role is granted the matching permissions
          (sales, stock, reports…); ask your administrator if you expected something here.
        </Alert>
      ) : (
        <WidgetGrid widgets={data?.widgets ?? []} />
      )}

      {customizing && catalog.data && data && (
        <LayoutEditorDialog
          open
          title="Customize my dashboard"
          catalog={catalog.data}
          initialWidgets={data.layout.widgets}
          saveLabel="Save my layout"
          saving={save.isPending}
          onSave={(w) => save.mutate(w)}
          onClose={() => setCustomizing(false)}
          footerLeft={data.hasPersonalLayout && (
            <Button color="warning" disabled={reset.isPending} onClick={() => reset.mutate()}>Restore assigned / default layout</Button>
          )}
        />
      )}
    </Box>
  );
}
